import classNames from 'classnames';
import { map, get } from 'lodash';
import PropTypes from 'prop-types';
import React from 'react';

import Accordion from 'components/Accordion';
import Button, { TYPES as BUTTON_TYPES } from 'components/Button';
import SizeButton from 'components/SizeButton';
import { SubBold } from 'components/Sub';
import { UPSELL_RECOMMENDATION_TYPES } from 'constants/cartUpsells';
import { mediumUp } from 'constants/mediaQueries';
import Theme from 'styles/theme';
import Size from 'utils/Size';

import AddButton from './AddButton';
import { SIZES_PER_ROW_MOBILE, SIZES_PER_ROW_DESKTOP, EVENTS } from './constants';

const SizeSelector = ({
  sizes,
  selectorName,
  productType,
  isAtcDisabled,
  isExpanded,
  label,
  selectedSize,
  handleAddToCart,
  handleSizeSelect,
  handleExpand,
  trackEvent,
}) => {
  const handleClick = (sizeLabel, isAvailable) => {
    trackEvent({
      eventAction: EVENTS.UPSELL,
      eventLabel: EVENTS.CLICK_SIZE_SELECTOR,
    });
    handleSizeSelect(sizeLabel, isAvailable);
  };

  return (
    <div className="SizeSelector">
      <div className="SizeSelector__wrapper">
        <button className="SizeSelector__drawer" onClick={handleExpand}>
          <SubBold className="SizeSelector__text">{selectorName}</SubBold>
          <AddButton isExpanded={isExpanded} />
        </button>
        <div className="SizeSelector__accordion">
          <Accordion isOpen={isExpanded}>
            <ul className="SizeSelector__grid">
              {map(sizes, size => {
                const isActive = get(sizes[selectedSize], 'variantId') === get(size, 'variantId');

                return (
                  <li
                    className={classNames('SizeSelector__grid-item', {
                      'SizeSelector__grid-item--2-column':
                        productType === UPSELL_RECOMMENDATION_TYPES.SOCKS,
                      'SizeSelector__grid-item--3-column':
                        productType !== UPSELL_RECOMMENDATION_TYPES.SOCKS &&
                        !Size.isSizeButtonSquare(get(size, 'label')),
                    })}
                    key={get(size, 'variantId')}
                  >
                    <SizeButton
                      label={get(size, 'label')}
                      available={get(size, 'isAvailable')}
                      active={isActive}
                      onClick={() => handleClick(get(size, 'label'), get(size, 'isAvailable'))}
                    />
                  </li>
                );
              })}
            </ul>
            <Button
              className="SizeSelector__atc"
              isFullWidth
              onClick={handleAddToCart}
              isDisabled={isAtcDisabled}
              type={BUTTON_TYPES.DEFAULT}
              isUppercase
            >
              {label}
            </Button>
          </Accordion>
        </div>
      </div>
      <style jsx>
        {`
          .SizeSelector {
            background-color: ${Theme.get('colors-grey')};
          }

          .SizeSelector__drawer {
            display: flex;
            align-items: center;
            cursor: pointer;
            width: 100%;
            height: 100%;
            background: none;
            color: inherit;
            border: none;
            font: inherit;
            cursor: pointer;
            outline: inherit;
            padding: ${Theme.get('spacing') * 2}px ${Theme.get('spacing') * 3}px 0;
          }

          .SizeSelector__grid {
            margin: ${Theme.get('spacing')}px ${Theme.get('spacing') / -2}px
              ${Theme.get('spacing') * 2}px;
          }

          .SizeSelector__accordion {
            padding: 0 ${Theme.get('spacing') * 3}px ${Theme.get('spacing') * 2}px;
          }

          .SizeSelector__grid-item {
            display: inline-block;
            width: ${100 / SIZES_PER_ROW_MOBILE}%;
            max-width: ${Theme.get('spacing') * 8.5}px;
            padding: 0 ${Theme.get('spacing') / 2}px ${Theme.get('spacing')}px;
            margin: 0;
          }

          @media ${mediumUp} {
            .SizeSelector__grid-item {
              width: ${100 / SIZES_PER_ROW_DESKTOP}%;
            }
          }

          .SizeSelector__grid-item--2-column {
            width: 50%;
            max-width: none;
          }

          .SizeSelector__grid-item--3-column {
            width: 33.3333%;
            max-width: none;
          }
        `}
      </style>
      <style jsx global>
        {`
          .Sub.SizeSelector__text {
            flex-grow: 1;
            text-align: left;
          }

          .SizeSelector__drawer:hover .AddButton__plus:before {
            transform: rotate(90deg);
          }

          .SizeSelector__drawer:hover .AddButton__plus:after {
            transform: rotate(180deg);
          }

          .SizeSelector__drawer:hover .AddButton__plus--expanded:after {
            transform: rotate(90deg);
          }

          .SizeSelector__grid-item--rectangle .SizeButton--selected .Sub {
            color: ${Theme.get('colors-white')};
          }
        `}
      </style>
    </div>
  );
};

const sizePropType = PropTypes.shape({
  isAvailable: PropTypes.bool,
  variantId: PropTypes.string,
  label: PropTypes.string,
  price: PropTypes.string,
});

SizeSelector.propTypes = {
  sizes: PropTypes.objectOf(sizePropType).isRequired,
  selectorName: PropTypes.string.isRequired,
  productType: PropTypes.oneOf(Object.values(UPSELL_RECOMMENDATION_TYPES)),
  isAtcDisabled: PropTypes.bool,
  isExpanded: PropTypes.bool,
  label: PropTypes.string,
  selectedSize: sizePropType,
  handleAddToCart: PropTypes.func,
  handleSizeSelect: PropTypes.func,
  handleExpand: PropTypes.func,
  trackEvent: PropTypes.func,
};

SizeSelector.defaultProps = {
  productType: UPSELL_RECOMMENDATION_TYPES.SHOES,
  selectedSize: null,
  isAtcDisabled: true,
  isExpanded: false,
  label: null,
  handleAddToCart: () => {},
  handleSizeSelect: () => {},
  handleExpand: () => {},
  trackEvent: () => {},
};

export default SizeSelector;
